'use client';

import ActionButtons from '@/components/ActionButtons';
import { useActionButtonsController } from '@/components/action-buttons/use-action-buttons-controller';
import type { ActionButtonsControllerValue } from '@/components/action-buttons/action-buttons-controller.types';
import BrandsTable from '@/components/BrandsTable';
import FeedbacksTable from '@/components/FeedbacksTable';
import Pagination from '@/components/pagination';
import ProductsTable from '@/components/ProductsTable';
import ReviewsOperationsProgress from '@/components/ReviewsOperationsProgress';
import SettingsRefreshButtons from '@/components/SettingsRefreshButtons';
import { ReviewsReconciliationProgressStrip } from '@/components/reviews-client-page/ReviewsReconciliationProgressStrip';
import { ReviewsClientPageController } from '@/components/reviews-client-page/use-reviews-client-page-controller';
import { AutoRepliesModalProvider } from '@/context/auto-replies-modal.context';
import {
    SelectedAutoRepliesModalProvider,
    useSelectedAutoRepliesModal,
} from '@/context/selected-auto-replies-modal.context';
import { SettingsModalProvider } from '@/context/settings-modal.context';
import { surfaceLayers } from '@/ui/overlay-layers';

type ReviewsClientPageViewProps = ReviewsClientPageController;

type ReviewsClientPageControlsRowProps = Pick<
    ReviewsClientPageController,
    | 'activeTable'
    | 'setActiveTable'
    | 'total'
    | 'reviewsPage'
    | 'reviewsOnPage'
    | 'setReviewsOnPage'
    | 'productsPage'
    | 'setProductsPage'
    | 'productsOnPage'
    | 'productsPageCount'
    | 'productsTotalRows'
    | 'brandsPage'
    | 'setBrandsPage'
    | 'brandsOnPage'
    | 'brandsPageCount'
    | 'brandsTotalRows'
    | 'rowsPerPagePresets'
    | 'setPage'
    | 'onProductsOnPageUpdate'
    | 'onBrandsOnPageUpdate'
> & {
    actionButtonsController: ActionButtonsControllerValue;
    controlsScope: 'top' | 'bottom';
};

const ReviewsClientPageControlsRow = ({
    activeTable,
    setActiveTable,
    total,
    reviewsPage,
    reviewsOnPage,
    setReviewsOnPage,
    productsPage,
    setProductsPage,
    productsOnPage,
    productsPageCount,
    productsTotalRows,
    brandsPage,
    setBrandsPage,
    brandsOnPage,
    brandsPageCount,
    brandsTotalRows,
    rowsPerPagePresets,
    setPage,
    onProductsOnPageUpdate,
    onBrandsOnPageUpdate,
    actionButtonsController,
    controlsScope,
}: ReviewsClientPageControlsRowProps) => {
    const { openSelectedAutoRepliesModal } = useSelectedAutoRepliesModal();
    const renderPaginationControl = () => {
        if (activeTable === 'reviews') {
            return (
                <Pagination
                    numOfPages={Math.max(1, Math.ceil(total / reviewsOnPage))}
                    currentPage={reviewsPage}
                    elementsOnPage={reviewsOnPage}
                    totalRows={total}
                    presetValues={rowsPerPagePresets}
                    onElementsOnPageUpdate={setReviewsOnPage}
                    setPage={setPage}
                />
            );
        }

        if (activeTable === 'products') {
            return (
                <Pagination
                    numOfPages={productsPageCount}
                    currentPage={productsPage}
                    elementsOnPage={productsOnPage}
                    totalRows={productsTotalRows}
                    presetValues={rowsPerPagePresets}
                    onElementsOnPageUpdate={onProductsOnPageUpdate}
                    setPage={setProductsPage}
                />
            );
        }

        return (
            <Pagination
                numOfPages={brandsPageCount}
                currentPage={brandsPage}
                elementsOnPage={brandsOnPage}
                totalRows={brandsTotalRows}
                presetValues={rowsPerPagePresets}
                onElementsOnPageUpdate={onBrandsOnPageUpdate}
                setPage={setBrandsPage}
            />
        );
    };

    const paginationControl = (
        <div className={controlsScope === 'bottom' ? 'shrink-0' : 'shrink-0 ml-auto'} data-controls-group="pagination">
            {renderPaginationControl()}
        </div>
    );
    const canRunSelectedAutoReplies =
        controlsScope === 'top' &&
        activeTable === 'reviews' &&
        actionButtonsController.buttonRowBindings.pickedReviewsCount > 0;

    return (
        <div className="w-full overflow-x-auto" data-controls-scope={controlsScope}>
            <div className="flex min-w-max items-center gap-2">
                {controlsScope === 'bottom' && paginationControl}
                <div
                    className={controlsScope === 'bottom' ? 'shrink-0 ml-auto' : 'shrink-0'}
                    data-controls-group="actions"
                >
                    <ActionButtons
                        activeTable={activeTable}
                        onSetActiveTable={setActiveTable}
                        controllerValue={actionButtonsController}
                        renderModals={controlsScope === 'bottom'}
                        controlsScope={controlsScope}
                    />
                </div>
                {controlsScope === 'top' && paginationControl}
                <div className="shrink-0" data-controls-group="settings">
                    <SettingsRefreshButtons
                        controlsScope={controlsScope}
                        selectedAutoRepliesRun={
                            canRunSelectedAutoReplies
                                ? {
                                      enabled: true,
                                      openSelectedAutoRepliesModal: () => openSelectedAutoRepliesModal(),
                                  }
                                : undefined
                        }
                    />
                </div>
            </div>
        </div>
    );
};

export const ReviewsClientPageView = ({
    total,
    reviewsPage,
    filters,
    reviewsOnPage,
    setReviewsOnPage,
    activeTable,
    setActiveTable,
    productsPage,
    setProductsPage,
    productsOnPage,
    productsPageCount,
    setProductsPageCount,
    productsTotalRows,
    setProductsTotalRows,
    brandsPage,
    setBrandsPage,
    brandsOnPage,
    brandsPageCount,
    setBrandsPageCount,
    brandsTotalRows,
    setBrandsTotalRows,
    rowsPerPagePresets,
    scrollContainerRef,
    topControlsContainerRef,
    onSortChanged,
    updateFilters,
    setPage,
    onProductsOnPageUpdate,
    onBrandsOnPageUpdate,
}: ReviewsClientPageViewProps) => {
    const actionButtonsController = useActionButtonsController({ activeTable });
    const controlsRowProps: ReviewsClientPageControlsRowProps = {
        activeTable,
        setActiveTable,
        total,
        reviewsPage,
        reviewsOnPage,
        setReviewsOnPage,
        productsPage,
        setProductsPage,
        productsOnPage,
        productsPageCount,
        productsTotalRows,
        brandsPage,
        setBrandsPage,
        brandsOnPage,
        brandsPageCount,
        brandsTotalRows,
        rowsPerPagePresets,
        setPage,
        onProductsOnPageUpdate,
        onBrandsOnPageUpdate,
        actionButtonsController,
        controlsScope: 'top',
    };

    const activeTableContent =
        activeTable === 'reviews' ? (
            <FeedbacksTable
                onSortChanged={onSortChanged}
                onFiltersChange={updateFilters}
                defaultFilterValues={filters}
            />
        ) : activeTable === 'products' ? (
            <ProductsTable
                productsPage={productsPage}
                productsOnPage={productsOnPage}
                setProductsPage={setProductsPage}
                setProductsPageCount={setProductsPageCount}
                setProductsTotalRows={setProductsTotalRows}
            />
        ) : (
            <BrandsTable
                brandsPage={brandsPage}
                brandsOnPage={brandsOnPage}
                setBrandsPage={setBrandsPage}
                setBrandsPageCount={setBrandsPageCount}
                setBrandsTotalRows={setBrandsTotalRows}
            />
        );

    return (
        <SettingsModalProvider>
            <AutoRepliesModalProvider>
                <SelectedAutoRepliesModalProvider>
                    <div className="w-full h-screen min-h-0 bg-gray-50 overflow-hidden flex flex-col">
                        <div className="w-full flex-1 min-h-0">
                            <div className="w-full h-full flex flex-col bg-white border border-slate-200 overflow-hidden">
                                <div ref={scrollContainerRef} className="min-h-0 flex-1 flex flex-col overflow-hidden">
                                    <div
                                        ref={topControlsContainerRef}
                                        className="sticky top-0 bg-white lg:static"
                                        style={{ zIndex: surfaceLayers.surfaceStickyRaised }}
                                    >
                                        <ReviewsClientPageControlsRow {...controlsRowProps} />
                                        <ReviewsReconciliationProgressStrip />
                                    </div>
                                    <div className="min-h-0 flex-1 overflow-auto">{activeTableContent}</div>
                                </div>
                                <div
                                    className="sticky bottom-0 bg-white lg:static"
                                    style={{ zIndex: surfaceLayers.surfaceStickyRaised }}
                                >
                                    <ReviewsClientPageControlsRow {...controlsRowProps} controlsScope="bottom" />
                                </div>
                            </div>
                        </div>
                        <ReviewsOperationsProgress />
                    </div>
                </SelectedAutoRepliesModalProvider>
            </AutoRepliesModalProvider>
        </SettingsModalProvider>
    );
};
